/* WeQuote Dev Board — click-to-edit for existing card fields (Lee, 27 Aug).
 * NO extra chips or rows: the fields already on a card (priority badge,
 * status pill, effort/est badge, customer + dev values, deadline) simply
 * become editable when clicked — an anchored dropdown appears (search for
 * customers + devs, date picker for deadline) and the layout never changes.
 * All edits use the same mutations as the edit screen (gates/stamps apply). */
import React, { useEffect, useRef, useState } from 'react'
import { useDevBoard } from '../DevBoardProvider'
import { rankMap, inPipelineF, sectionRank } from '../core/logic'
import * as M from '../core/mutations'
import type { Feature } from '../core/types'
import { DEVCOLS, DL, TIME_OPTS, MODULE_OPTS, POOLCOLS, statusLabels } from '../core/constants'
import { todayISO } from '../core/dates'
import { fetchCustomers, type CrmCustomer } from '../core/api'

export type QEField = 'pri' | 'status' | 'time' | 'cust' | 'dev' | 'deadline' | 'module' | 'start' | 'progress' | 'pos'

const Pop: React.FC<{ anchor: DOMRect; onClose: () => void; children: React.ReactNode }> = ({ anchor, onClose, children }) => {
  const ref = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const down = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) onClose() }
    const key = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    const scroll = (e: Event) => { if (ref.current && !ref.current.contains(e.target as Node)) onClose() }
    document.addEventListener('mousedown', down)
    document.addEventListener('keydown', key)
    window.addEventListener('scroll', scroll, true)
    return () => { document.removeEventListener('mousedown', down); document.removeEventListener('keydown', key); window.removeEventListener('scroll', scroll, true) }
  }, [onClose])
  const top = Math.min(anchor.bottom + 4, window.innerHeight - 300)
  const left = Math.min(anchor.left, window.innerWidth - 250)
  return <div ref={ref} className="qe-pop" style={{ top, left }} onClick={(e) => e.stopPropagation()}>{children}</div>
}

export function useQuickEdit(feat: Feature) {
  const { mutate, reqDatalists, cards: cardsAll } = useDevBoard()
  const [open, setOpen] = useState<QEField | null>(null)
  const [anchor, setAnchor] = useState<DOMRect | null>(null)
  const [q, setQ] = useState('')
  const [crmList, setCrmList] = useState<CrmCustomer[]>([])
  useEffect(() => { if (open === 'cust') fetchCustomers().then(setCrmList) }, [open])
  const close = () => { setOpen(null); setQ('') }
  const start = (f: QEField) => (e: React.MouseEvent) => {
    e.stopPropagation(); e.preventDefault()
    if (open === f) { close(); return }
    setAnchor((e.currentTarget as HTMLElement).getBoundingClientRect()); setQ(''); setOpen(f)
  }
  const pick = (fn: (fs: Feature[]) => M.MutResult) => { mutate(fn); close() }

  let editor: React.ReactNode = null
  if (open && anchor) {
    const SL = statusLabels()
    const ql = q.trim().toLowerCase()
    if (open === 'pri') editor = (
      <Pop anchor={anchor} onClose={close}>
        {(['high', 'medium', 'low'] as const).map((p) => (
          <div key={p} className={'qe-opt' + (feat.pri === p ? ' on' : '')} onClick={() => pick((fs) => M.upd(fs, feat.id, 'pri', p))}>
            <span className={'badge priority-' + p}>{p === 'high' ? 'High' : p === 'medium' ? 'Medium' : 'Low'}</span>
          </div>
        ))}
      </Pop>
    )
    if (open === 'status') editor = (
      <Pop anchor={anchor} onClose={close}>
        {Object.keys(SL).filter((k) => k !== 'archived').map((k) => (
          <div key={k} className={'qe-opt' + (feat.status === k ? ' on' : '')} onClick={() => pick((fs) => (POOLCOLS.indexOf(k) > -1 ? M.moveToBand(fs, feat.id, k) : M.moveStatus(fs, feat.id, k)))}>{SL[k]}</div>
        ))}
      </Pop>
    )
    if (open === 'module') editor = (
      <Pop anchor={anchor} onClose={close}>
        {MODULE_OPTS.map((m) => (
          <div key={m} className={'qe-opt' + (feat.module === m ? ' on' : '')} onClick={() => pick((fs) => M.upd(fs, feat.id, 'module', m))}>{m}</div>
        ))}
      </Pop>
    )
    if (open === 'time' && feat.multiStage) editor = (
      <Pop anchor={anchor} onClose={close}>
        <div className="qe-lockednote">This card is staged — its estimate is the sum of the stage estimates. Edit them on the card.</div>
      </Pop>
    )
    if (open === 'time' && !feat.multiStage) editor = (
      <Pop anchor={anchor} onClose={close}>
        {TIME_OPTS.map((o) => (
          <div key={o.val || 'tbd'} className={'qe-opt' + (feat.time === o.val ? ' on' : '')} onClick={() => pick((fs) => M.updTime(fs, feat.id, o.val))}>{o.label}</div>
        ))}
      </Pop>
    )
    if (open === 'dev') {
      const matches = ['', ...DEVCOLS].filter((d) => !ql || (d === '' ? 'unassigned' : DL[d] || d).toLowerCase().includes(ql))
      editor = (
        <Pop anchor={anchor} onClose={close}>
          <input className="qe-search" autoFocus placeholder="Search devs…" value={q} onChange={(e) => setQ(e.target.value)} />
          {matches.map((d) => (
            <div key={d || 'none'} className={'qe-opt' + (feat.dev === d ? ' on' : '')} onClick={() => pick((fs) => M.upd(fs, feat.id, 'dev', d))}>{d === '' ? 'Unassigned' : DL[d] || d}</div>
          ))}
        </Pop>
      )
    }
    if (open === 'cust') {
      const crmNames = new Set(crmList.map((c) => c.name.toLowerCase()))
      const current = Array.from(new Set((feat.requests || []).map((r) => (r.company || '').trim()).filter(Boolean)))
      const paid = crmList.filter((c) => c.kind === 'paid' && (!ql || c.name.toLowerCase().includes(ql))).slice(0, 8)
      const deals = crmList.filter((c) => c.kind === 'deal' && (!ql || c.name.toLowerCase().includes(ql))).slice(0, 6)
      const matches = reqDatalists.companies.filter((c) => !crmNames.has(c.toLowerCase()) && (!ql || c.toLowerCase().includes(ql))).slice(0, 8)
      const typedNew = q.trim() && !reqDatalists.companies.some((c) => c.toLowerCase() === ql) && !crmNames.has(ql)
      editor = (
        <Pop anchor={anchor} onClose={close}>
          <input className="qe-search" autoFocus placeholder="Search customers…" value={q} onChange={(e) => setQ(e.target.value)} />
          {current.length > 0 && (
            <div className="qe-current">
              {current.map((c) => (
                <span key={c} className="qe-cur-chip">{c}
                  <b title="Remove this customer" onClick={() => mutate((fs) => M.upd(fs, feat.id, 'requests', (fs.find((x) => x.id === feat.id)?.requests || []).filter((r) => (r.company || '').trim() !== c)))}> ×</b>
                </span>
              ))}
            </div>
          )}
          {paid.length > 0 && <div className="cp-hd">Paid customers</div>}
          {paid.map((c) => (
            <div key={'p' + c.id} className={'qe-opt' + (current.indexOf(c.name) > -1 ? ' on' : '')} onClick={() => pick((fs) => M.addRequest(fs, feat.id, c.name, '', todayISO(), { id: c.id, kind: 'paid' }))}>{c.name}<span className="cp-tag paid">CRM</span></div>
          ))}
          {deals.length > 0 && <div className="cp-hd">CRM leads</div>}
          {deals.map((c) => (
            <div key={'d' + c.id} className={'qe-opt' + (current.indexOf(c.name) > -1 ? ' on' : '')} onClick={() => pick((fs) => M.addRequest(fs, feat.id, c.name, '', todayISO(), { id: c.id, kind: 'deal' }))}>{c.name}<span className="cp-tag lead">Lead</span></div>
          ))}
          {matches.length > 0 && <div className="cp-hd">On this board</div>}
          {matches.map((c) => (
            <div key={c} className={'qe-opt' + (current.indexOf(c) > -1 ? ' on' : '')} onClick={() => pick((fs) => M.addRequest(fs, feat.id, c, '', todayISO()))}>{c}</div>
          ))}
          {typedNew && <div className="qe-opt qe-new" onClick={() => pick((fs) => M.addRequest(fs, feat.id, q.trim(), '', todayISO()))}>+ Add “{q.trim()}”</div>}
        </Pop>
      )
    }
    if (open === 'pos' && sectionRank(feat) === 0) {
      // Committed cards order automatically by projected completion (Lee, 2 Sep)
      editor = (
        <Pop anchor={anchor} onClose={close}>
          <div className="qe-lockednote">Committed cards are ordered by the roadmap — earliest projected completion first. Adjust the schedule to change the order.</div>
        </Pop>
      )
    }
    if (open === 'pos' && sectionRank(feat) !== 0) {
      // v2 §6 decision 2: positions are offered WITHIN the card's band only
      const ordered = cardsAll.filter(inPipelineF).sort((a, b) => sectionRank(a) - sectionRank(b) || a.po - b.po)
      const myRank = rankMap(cardsAll)[feat.id] || 1
      const lo = ordered.findIndex((f) => sectionRank(f) === sectionRank(feat)) + 1
      let hi = lo
      while (hi < ordered.length && sectionRank(ordered[hi]) === sectionRank(feat)) hi++
      editor = (
        <Pop anchor={anchor} onClose={close}>
          <div className="qe-datewrap">
            <span className="qe-poslbl">Move within this band</span>
            <select autoFocus value={myRank} onChange={(e) => pick((fs) => M.moveToPosition(fs, feat.id, parseInt(e.target.value, 10)))}>
              {Array.from({ length: hi - lo + 1 }, (_, i) => lo + i).map((i) => <option key={i} value={i}>{i}</option>)}
            </select>
          </div>
        </Pop>
      )
    }
    if (open === 'start') editor = (
      <Pop anchor={anchor} onClose={close}>
        <div className="qe-datewrap">
          <input type="date" autoFocus value={feat.start || ''} max={feat.deadline || undefined}
            onChange={(e) => { mutate((fs) => M.setDate(fs, feat.id, 'start', e.target.value)) }} />
          <button className="btn-s" onClick={() => pick((fs) => M.setDate(fs, feat.id, 'start', ''))}>Clear</button>
        </div>
      </Pop>
    )
    if (open === 'progress' && feat.multiStage) editor = (
      <Pop anchor={anchor} onClose={close}>
        <div className="qe-lockednote">Progress on a staged card = share of stages complete — tick stages done on the card.</div>
      </Pop>
    )
    if (open === 'progress' && !feat.multiStage) editor = (
      <Pop anchor={anchor} onClose={close}>
        <div className="qe-progwrap">
          <input type="range" min={0} max={100} step={5} defaultValue={feat.progress || 0}
            onMouseUp={(e) => mutate((fs) => M.updProgress(fs, feat.id, (e.target as HTMLInputElement).value))}
            onTouchEnd={(e) => mutate((fs) => M.updProgress(fs, feat.id, (e.target as HTMLInputElement).value))} />
          <div className="qe-progbtns">
            {[0, 25, 50, 75, 100].map((v) => (
              <button key={v} className={'btn-s' + (feat.progress === v ? ' on' : '')} onClick={() => pick((fs) => M.updProgress(fs, feat.id, v))}>{v}%</button>
            ))}
          </div>
        </div>
      </Pop>
    )
    if (open === 'deadline') editor = (
      <Pop anchor={anchor} onClose={close}>
        <div className="qe-datewrap">
          <input type="date" autoFocus value={feat.deadline || ''} min={feat.start || undefined}
            onChange={(e) => { mutate((fs) => M.setDate(fs, feat.id, 'deadline', e.target.value)) }} />
          <button className="btn-s" onClick={() => pick((fs) => M.setDate(fs, feat.id, 'deadline', ''))}>Clear</button>
        </div>
      </Pop>
    )
  }
  return { start, editor }
}
